import React, { useState, useEffect, useCallback } from "react";
import { message, Input, Table, Button, Popconfirm, Radio, Space, Switch } from "antd";
import { Link } from "react-router-dom";
import { EyeOutlined, EditOutlined, DeleteOutlined, UndoOutlined } from "@ant-design/icons";

import { getProjects, deleteProject, unArchiveProject, archiveProject } from "../../api/project";
import { UserRole } from "../../lib/constants";

import PageHeader from "../../components/shared/PageHeader";
import BlueButton from "../../components/shared/BlueButton";
import StatusBadge from "../../components/shared/StatusBadge";
import { format } from "date-fns";

const { Search } = Input;

export default function ProjectsView({ user }) {
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(null);
  const [searchClientId, setSearchClientId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const perPage = 10;

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e);
  }

  const columns = [
    {
      title: "SN",
      dataIndex: "id",
      key: "id",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Project",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Client",
      key: "client",
      dataIndex: "client",
      render: (text, record) => <Link to={`/clients/${record.client.id}`}>{record.client.name}</Link>,
    },
    {
      title: "Start Date",
      dataIndex: "startAt",
      key: "startDate",
      render: (text) => format(new Date(text), "dd MMM yyyy"),
    },
    {
      title: "End Date",
      dataIndex: "endAt",
      key: "endDate",
      render: (text) => format(new Date(text), "dd MMM yyyy"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filteredValue: statusFilter,
      filtered: statusFilter !== null,
      filterDropdown: <div className="p-2 flex flex-col space-y-2">
        <Radio.Group value={statusFilter} onChange={(e) => handleStatusFilterChange(e.target.value)}>
          <div className="space-y-1.5 flex flex-col">
            <Radio value="todo">Todo</Radio>
            <Radio value="processing">Processing</Radio>
            <Radio value="complete">Complete</Radio>
          </div>
        </Radio.Group>
        <Button type="link" onClick={() => handleStatusFilterChange(null)}>Reset</Button>
      </div>,
      render: (text) => <StatusBadge status={text} />,
    },
    {
      title: "Actions",
      key: "action",
      render: (text, record) => {
        const isArchived = record.isArchived;

        return (
          <div className="flex gap-2">
            <Link to={`/projects/${record.id}`}>
              <Button type="text" icon={<EyeOutlined />} />
            </Link>
            {user?.role !== UserRole.AccountManager && (
              <>
                <Link to={`/projects/form/${record.id}`}>
                  <Button type="text" icon={<EditOutlined />} />
                </Link>
                {/* Archive or Unarchive Project */}
                {isArchived ? (
                  // Unarchive/Restore button for archived projects
                  <Popconfirm
                    title="Unarchive the project"
                    description="Are you sure to unarchive this project?"
                    onConfirm={async () => {
                      await unArchiveProject(record.id);
                      message.success("Project unarchived successfully");
                      await fetchProjects({ page, perPage, q: searchQuery, clientId: searchClientId, status: statusFilter, showArchived: showArchived });
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button size="small" icon={<UndoOutlined />} style={{ color: '#52c41a', borderColor: '#52c41a' }}>
                      Unarchive
                    </Button>
                  </Popconfirm>
                ) : (
                  // Archive button for active projects
                  <Popconfirm
                    title="Archive the project"
                    description="Are you sure to archive this project?"
                    onConfirm={async () => {
                      await archiveProject(record.id);
                      message.success("Project archived successfully");
                      await fetchProjects({ page, perPage, q: searchQuery, clientId: searchClientId, status: statusFilter, showArchived: showArchived });
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button size="small" icon={<DeleteOutlined />} danger>
                      Archive
                    </Button>
                  </Popconfirm>
                )}
              </>
            )}
          </div>
        )
      },
    }
  ]

  const fetchProjects = useCallback(
    async ({ page, q, clientId, status, showArchived }) => {
      try {
        setIsLoading(true);
        const { total, results } = await getProjects({
          page,
          perPage,
          q,
          clientId,
          status,
          showArchived
        });
        setTotal(total);
        setProjects(results);
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        message.error("Something went wrong");
        setIsLoading(false);
      }
    }, []);

  useEffect(() => {
    fetchProjects({
      page,
      perPage,
      q: searchQuery,
      clientId: searchClientId,
      status: statusFilter,
      showArchived: showArchived
    });
  }, [fetchProjects, page, perPage, searchClientId, searchQuery, statusFilter, showArchived]);

  return (
    <main className="space-y-6  min-h-full flex flex-col">
      <PageHeader title="Projects" />

      <div className="flex-grow bg-white ml-6 p-6 ">
        <div className="flex flex-col space-y-6">
          <div className="flex justify-between">
            {user?.role !== UserRole.AccountManager && (
              <Link to="/projects/form">
                <BlueButton type="primary" className="bg-[#1890FF]" >
                  Add New Project
                </BlueButton>
              </Link>
            )}
            <div className="flex gap-4 items-center">

              <div className="flex gap-2 items-center">
                <span>Show Archived:</span>
                <Switch
                  checked={showArchived}
                  onChange={(checked) => {
                    setShowArchived(checked);
                  }}
                />
              </div>
              <Search
                placeholder="Search Project"
                style={{
                  width: 200,
                }}
                allowClear
                onSearch={(q) => setSearchQuery(q)}
              />
            </div>
          </div>


          <section>
            <Table
              dataSource={projects}
              columns={columns}
              loading={isLoading}
              pagination={{ total, pageSize: perPage, current: page, onChange: (page) => setPage(page) }}
            />
          </section>

        </div>
      </div>
    </main>
  );
}
